import type { EvaluationRule } from './EvaluationRule';

export class RulesProvider implements ForRuleProviding {
  private evaluationRules: EvaluationRule[];

  private constructor(evaluationRules: EvaluationRule[]) {
    this.evaluationRules = evaluationRules;
  }

  static fromRules(rules: EvaluationRule[]): RulesProvider {
    return new RulesProvider(rules);
  }

  public getRule<TRuleName>(
    ruleName: TRuleName extends EvaluationRule['calculationProperty']
      ? TRuleName
      : never
  ): Extract<EvaluationRule, { calculationProperty: TRuleName }> | undefined {
    return this.evaluationRules.find(
      rule => rule.calculationProperty.toLocaleLowerCase() === ruleName.toLocaleLowerCase()
    ) as
      | Extract<EvaluationRule, { calculationProperty: TRuleName }>
      | undefined;
  }

  public forRule: ForRuleFunction = (ruleName, callbacks) => {
    const rule = this.getRule(ruleName);
    if (rule) {
      return callbacks.found(rule);
    } else {
      return callbacks.missing();
    }
  };
}

export type ForRuleProviding = {
  getRule: GetRuleFunction;
  forRule: ForRuleFunction;
};

type ForRuleFunction = <TCallbackResult, TRuleName>(
  ruleName: TRuleName extends EvaluationRule['calculationProperty']
    ? TRuleName
    : never,
  callbacks: {
    found: (
      rule: Extract<EvaluationRule, { calculationProperty: TRuleName }>
    ) => TCallbackResult;
    missing: () => TCallbackResult;
  }
) => TCallbackResult;

type GetRuleFunction = <TRuleName>(
  ruleName: TRuleName extends EvaluationRule['calculationProperty']
    ? TRuleName
    : never
) => Extract<EvaluationRule, { calculationProperty: TRuleName }> | undefined;
